import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
    BooleanField,
    ChipField,
    Create,
    Datagrid,
    /*
        DatagridConfigurable,
    */
    DateField,
    Edit,
    EditButton,
    EmailField,
    FormTab,
    FunctionField,
    FormDataConsumer,
    DateInput,
    List,
    Pagination,
    ReferenceField,
    SelectInput,
    SimpleForm,
    TabbedForm,
    TextField,
    TextInput,
    SearchInput,
    downloadCSV,
    useRecordContext,
    useGetList,
    useNotify,
    ArrayInput,
    SimpleFormIterator,
} from 'react-admin';
import {
    Button,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    CardActions, Box,
    IconButton, Link
} from "@mui/material";
import { ProjectStatusIcon } from './ProjectStatusIcon';
import { unparse as convertToCSV } from 'papaparse/papaparse.min';
import {
    EmailOutlined,
    FileCopyOutlined,
    Visibility,
    Warning,
    //VisibilityOff,
} from '@mui/icons-material'
import moment from "moment";
import { isZipOutOfArea } from '../../utilities/zipValidation';

const status = [
    { id: 0, name: 'abgelehnt' },
    { id: 1, name: 'wartend' },
    { id: 2, name: 'freigegeben' }
];

// Transform API response for logo to match react-admin expectations (https://github.com/marmelab/react-admin/issues/2077#issuecomment-516821629)
/*
function formatImageUrlString(value) {
    if (!value || typeof value === "string") { // Value is null or the url string from the backend, wrap it in an object so the form input can handle it
        return {url: value};
    } else {  // Else a new image is selected which results in a value object already having a preview link under the url key
        return value;
    }
}
*/

const ProjectGallery = () => {
    const record = useRecordContext();
    return (
        <>
            {record.projectGallery && record.projectGallery.map((imageURL, index) => {
                return (
                    <Grid item xs={12} lg={6} key={index}>
                        <Card style={{ display: 'flex' }}>
                            <CardMedia
                                style={{ minWidth: "33%", flexGrow: 1 }}
                                image={imageURL.url}
                                title={`Projektgalleriebild Nr. ${index}`}
                                alt={`Projektgalleriebild Nr. ${index}`}
                            />
                            <Box style={{ display: 'flex', flexDirection: 'column', flexShrink: 1, maxWidth: "67%" }}>
                                <CardContent style={{ flex: '1 0 auto' }}>
                                    <Typography color="textSecondary"
                                        className="mb-4">
                                        {`Bild Nr. ${index}`}
                                    </Typography>
                                    <TextInput label="Alternativtext/Bildbeschreibung" fullWidth
                                        source={`projectGallery.${index}.alt`}
                                        name="Alternativtext/Bildbeschreibung" />
                                    <Link variant="body2" href={imageURL.url} target="_blank" rel="noopener noreferrer" style={{ wordWrap: "break-word" }}>
                                        {imageURL.url}
                                    </Link>
                                </CardContent>
                                <CardActions>
                                    <Button size="small" color="primary" disabled>
                                        Entfernen
                                    </Button>
                                </CardActions>
                            </Box>
                        </Card>
                    </Grid>
                )
            })}
        </>
    )
};

const ProjectsPagination = props => <Pagination rowsPerPageOptions={[500]} {...props} />;

export const ProjectList = props => {

    const { data: optionsData } = useGetList('options');
    const allowedCodes = (
        optionsData &&
        optionsData[0] &&
        optionsData[0].projectForm &&
        optionsData[0].projectForm.contestantZipcode &&
        optionsData[0].projectForm.contestantZipcode.elementConfig
    ) ? optionsData[0].projectForm.contestantZipcode.elementConfig.allowedCodes : [];

    const exporter = projects => {
        let inputString = process.env.REACT_APP_SERVER_URL;
        let secondSlashIndex = inputString.indexOf("/", inputString.indexOf("/") + 1);
        let firstDotIndex = inputString.indexOf(".", secondSlashIndex);
        let result = inputString.substring(secondSlashIndex + 1, firstDotIndex);
        let currentDate = moment().format("YYMMDDTHHmmss");

        const csv = convertToCSV({
            data: projects,
            fields: [
                '_id',
                'status',
                'contestantName',
                'projectTitle',
                'creationDate',
                'projectCategory',
                'contestantStreet',
                'contestantZipcode',
                'contestantCity',
                'author',
                'referrer',
                'voteCount',
                'voteCountFinale'
            ]
        });
        downloadCSV(csv, `${result}-export-users-${currentDate}`); // download as 'posts.csv` file
    }

    // State setup
    //const [value, setValue] = useState('');
    const [visible, setVisibility] = useState(false);

    // List filters setup
    const projectsFilters = [<SearchInput source="q" alwaysOn />];

    // State handling for copy-to-clipboard
    const notify = useNotify();
    const handleCopy = () => {
        //setCopied(true);
        notify('In Zwischenablage kopiert', { type: "success" });
    };

    const handleUncover = () => {
        setVisibility(true);
    }

    const UncoveredEmailField = () => {
        const record = useRecordContext();
        if (!record) return null;
        return <EmailField source={record.email} />
    }

    const EmailActionButtons = () => {
        const record = useRecordContext();
        if (!record) return null;
        return (
            <div style={{ minWidth: '150px' }}>
                <IconButton onClick={handleUncover}>
                    <Visibility fontSize="small" />
                </IconButton>
                <IconButton component="a" href={`mailto:${record.email}`}><EmailOutlined fontSize="small" /></IconButton>
                <CopyToClipboard text={record.email} onCopy={handleCopy}>
                    <IconButton><FileCopyOutlined fontSize="small" /></IconButton>
                </CopyToClipboard>
            </div>
        )
    }

    const CustomContestantNameField = () => {
        const record = useRecordContext();
        return (
            <a
                href={`${process.env.REACT_APP_SITE_URL}/projekte/${record._id}`}
                target="_blank"
                rel="noopener noreferrer">
                {record.contestantName}
            </a>
        )
    };

    const CommentField = () => {
        const record = useRecordContext();
        if (record.commentCount > 0) {
            return <ChipField label="Kommentare" source="commentCount" color="primary" size="small" />
        }
        return <ChipField label="Kommentare" source="commentCount" size="small" />
    };

    const ConditionalField = () => {
        const record = useRecordContext();
        if (record.projectCategory) {
            return <TextField label="Kategorie" source="projectCategory" />
        }
    }

    return (
        <List
            {...props}
            exporter={exporter}
            sort={{ field: 'creationDate', order: 'DESC' }}
            pagination={<ProjectsPagination />}
            filters={projectsFilters}
            perPage={200}>
            <Datagrid optimized size="small">
                <ProjectStatusIcon label="Status" source="status" />
                <ConditionalField source="projectCategory" label="Kategorie" />
                <BooleanField label="Finalist?" source="isFinalist" />
                <CustomContestantNameField />
                <FunctionField
                    label="PLZ"
                    render={record => {
                        if (!record.contestantZipcode) return null;
                        const isWarning = isZipOutOfArea(record.contestantZipcode, allowedCodes);
                        const style = isWarning ? {
                            color: '#ed6c02', // MUI warning.main
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center'
                        } : {};
                        return (
                            <span style={style}>
                                {record.contestantZipcode}
                                {isWarning &&
                                    <Warning color="warning" sx={{ fontSize: '1rem', ml: 0.5 }} titleAccess="PLZ außerhalb des Gebiets" />}
                            </span>
                        );
                    }}
                />
                <TextField label="Projekttitel" source="projectTitle" />
                <TextField label="Fingerprint-Stimmen" source="voteCount" />
                <TextField label="SMS-Stimmen" source="voteCountFinale" />
                <DateField label="Bewerbungsdatum" source="creationDate" />
                <ReferenceField
                    label="Ansprechpartner"
                    source="author"
                    reference="users">
                    <FunctionField render={author => `${author.salutation} ${author.firstname} ${author.lastname}`} />
                </ReferenceField>
                <ReferenceField
                    label="E-Mail-Adresse"
                    link={false}
                    source="author"
                    reference="users">
                    {visible ? <UncoveredEmailField /> : <EmailActionButtons />}
                </ReferenceField>
                <EditButton />
            </Datagrid>

        </List>
    )
};

export const ProjectEdit = props => {
    const { data, isLoading, error } = useGetList('options');
    if (error) {
        return <p>Es ist ein Fehler aufgetreten:</p>
    }
    if (isLoading) {
        return 'Lade Kategorien...';
    }

    let choicesCategories = []
    let categoriesExist = false
    if (data[0].projectForm.projectCategory) {
        categoriesExist = true
        choicesCategories = data[0].projectForm.projectCategory.elementConfig.options.map(category => ({
            id: category.value,
            name: category.displayValue,
        }));
    }

    let allowedCodes = [];
    if (data[0].projectForm && data[0].projectForm.contestantZipcode) {
        allowedCodes = data[0].projectForm.contestantZipcode.elementConfig.allowedCodes || [];
    }

    return (
        <Edit {...props}>
            <TabbedForm>
                <FormTab label="Teilnehmerdaten">
                    <Grid container spacing={2} sx={{ p: 2 }}>
                        <Grid container xs={12} lg={9} spacing={2}>
                            <Grid item xs={12}>
                                <h5>Projektstatus und -adresse</h5>
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <DateInput disabled fullWidth label="Bewerbungsdatum" source="creationDate" />
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <SelectInput fullWidth source="status" style={{ marginTop: 0 }} choices={status} />
                            </Grid>
                            {categoriesExist &&
                                <Grid item xs={12} lg={4}>
                                    <SelectInput fullWidth label="Projektkategorie" style={{ marginTop: 0 }}
                                        source="projectCategory" choices={choicesCategories} />
                                </Grid>
                            }
                            <Grid item xs={12} lg={6}>
                                <TextInput fullWidth label="Teilnehmername" source="contestantName" />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <TextInput fullWidth label="Projekttitel" source="projectTitle" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextInput fullWidth label="Straße" source="contestantStreet" />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormDataConsumer>
                                    {({ formData }) => {
                                        const isWarning = isZipOutOfArea(formData.contestantZipcode, allowedCodes);
                                        return (
                                            <TextInput
                                                fullWidth
                                                label="PLZ"
                                                source="contestantZipcode"
                                                sx={isWarning ? {
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#ed6c02' }, // warning.main
                                                        '&:hover fieldset': { borderColor: '#ed6c02' },
                                                        '&.Mui-focused fieldset': { borderColor: '#ed6c02' },
                                                    },
                                                    '& .MuiInputLabel-root': { color: '#ed6c02' }
                                                } : undefined}
                                                helperText={isWarning ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', color: '#ed6c02' }}>
                                                        <Warning fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} /> Außerhalb des Gebiets
                                                    </span>
                                                ) : null}
                                            />
                                        )
                                    }}
                                </FormDataConsumer>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextInput fullWidth label="Ort" source="contestantCity" />
                            </Grid>
                        </Grid>
                        <Grid container xs={12} spacing={2}>
                            <Grid item xs={12}>
                                <h5>Projektgalerie</h5>
                            </Grid>
                            <Grid item container spacing={2}>
                                <ProjectGallery />
                            </Grid>
                            <Grid item xs={12}>
                                <h5>Projektdaten</h5>
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <TextInput multiline fullWidth label="Antwort auf die 1. Frage" rows={16}
                                    source="contestantAnswer1" />
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <TextInput multiline fullWidth label="Antwort auf die 2. Frage" rows={16}
                                    source="contestantAnswer2" />
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <TextInput multiline fullWidth label="Antwort auf die 3. Frage" rows={16}
                                    source="contestantAnswer3" />
                            </Grid>
                        </Grid>
                    </Grid>
                </FormTab>
                <FormTab label="Kommentare">
                    <Grid container spacing={2} sx={{ p: 2 }}>
                        <ArrayInput label="Kommentare" source="comments">
                            <SimpleFormIterator inline>
                                <DateField disabled showTime label="Datum" source="createdAt" />
                                <ReferenceField
                                    label="Author"
                                    source="author"
                                    reference="users">
                                    <FunctionField render={author => `${author.firstname} ${author.lastname}`} />
                                </ReferenceField>
                                <TextField label="Kommentar" source="text" />
                            </SimpleFormIterator>
                        </ArrayInput>
                    </Grid>
                </FormTab>
                <FormTab label="Projektgallerie">
                    <Grid container spacing={2} sx={{ p: 2 }}>
                        <ProjectGallery />
                    </Grid>
                </FormTab>
            </TabbedForm>
        </Edit>
    )
};

export const ProjectCreate = props => (
    <Create {...props}>
        <SimpleForm>
            <Grid container>
                <Grid item xs={12}>
                    <SelectInput fullWidth source="status" choices={status} />
                </Grid>
                <Grid item md={6}>
                    <TextInput fullWidth label="Teilnehmername" source="contestantName" />
                </Grid>
                <Grid item md={6}>
                    <TextInput fullWidth label="Projekttitel" source="projectTitle" />
                </Grid>
                <Grid item xs={12}>
                    <TextInput multiline label="Antwort auf die 1. Frage" source="contestantAnswer1" />
                </Grid>
                <Grid item xs={12}>
                    <TextInput multiline label="Antwort auf die 2. Frage" source="contestantAnswer2" />
                </Grid>
            </Grid>
        </SimpleForm>
    </Create>
);
